import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import GenericPage from '../../components/GenericPage';
import {
    fetchCashmemos,
    createCashMemo,
    updateCashMemo,
    deleteCashMemo,
    setPagination,
    setSort,
    searchCashmemos,
    setDefaultFilter,
    changeCondition,
    addNewCondition,
    updateOptionRule,
    updateOptionValue,
    removeCondition,
    saveFilterCondition,
    setSelected,
    resetFiltersAndPagination
} from '../../store/modules/cashmemo/cashmemoActions';
import CashMemoForm from '../../components/pageModule/Cashmemos/CashMemoForm';
import { useHasPermission } from '../../utils/permissionUtils';

const MySwal = withReactContent(Swal);

const Cashmemos = ({
    excludedActions = [],
    onSelectedRowsChange = () => { },
    onClickedRow = () => { },
    externalCondition = {},
    selectedIds = [] }) => {

    const cashmemoState = useSelector(state => state.cashmemo);
    const dispatch = useDispatch();
    const hasCreatePermission = useHasPermission(['create cash memos']);
    const hasUpdatePermission = useHasPermission(['update cash memos']);
    const hasViewPermission = useHasPermission(['view cash memos']);
    useEffect(() => {
        return () => {
            dispatch(resetFiltersAndPagination());
        };
    }, []);

    const handleCreate = async (formData) => {
        try {
            const response = await dispatch(createCashMemo(formData));
            toast.success('CashMemo created successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error creating cashmemo', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleUpdate = async (id, formData) => {
        try {
            const response = await dispatch(updateCashMemo(id, formData));
            toast.success('CashMemo updated successfully', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            return response;
        } catch (error) {
            toast.error(error.message || 'Error updating cashmemo', {
                position: "top-right",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            throw error;
        }
    };

    const handleDelete = async (id) => {
        const result = await MySwal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonText: 'No, cancel!',
            reverseButtons: true
        });

        if (result.isConfirmed) {
            try {
                await dispatch(deleteCashMemo(id));
                toast.success('CashMemo deleted successfully', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return true;
            } catch (error) {
                toast.error(error.message || 'Error deleting cashmemo', {
                    position: "top-right",
                    autoClose: 3000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                });
                return false;
            }
        }
        return false;
    };

    const columns = [
        {
            name: 'Voucher Number',
            selector: row => row.voucher_number,
            minWidth: '170px'
        },
        {
            name: 'Customer Name',
            selector: row => row.customer_name ?? '-',
        },
        {
            name: 'Voucher Date',
            selector: row => row.voucher_date,
            sortable: true,
            sortField: 'delivery_notes.delivery_date',
        },
        {
            name: 'Net Total',
            selector: row => row.net_total
        }
    ];


    const actions = {
        fetch: (queryFilter, activeFilter, shouldUseFilter, externalCondition) => dispatch(fetchCashmemos(queryFilter, activeFilter, shouldUseFilter, externalCondition)),
        create: handleCreate,
        update: handleUpdate,
        delete: handleDelete,
        search: (search) => dispatch(searchCashmemos(search)),
        setPagination: (pagination) => dispatch(setPagination(pagination)),
        setSort: (sort) => dispatch(setSort(sort)),
        setDefaultFilter: (filter) => dispatch(setDefaultFilter(filter)),
        changeCondition: (index, condition) => dispatch(changeCondition(index, condition)),
        addNewCondition: (rule) => dispatch(addNewCondition(rule)),
        updateOptionRule: (index, rule) => dispatch(updateOptionRule(index, rule)),
        updateOptionValue: (index, value) => dispatch(updateOptionValue(index, value)),
        removeCondition: (index) => dispatch(removeCondition(index)),
        saveFilterCondition: (filter) => dispatch(saveFilterCondition(filter)),
        setSelected: (selectedRows) => dispatch(setSelected(selectedRows)),
    };

    let defaultAvailableActions = ['search', 'filter'];
    if (hasCreatePermission) {
        defaultAvailableActions = ['create', ...defaultAvailableActions];
    }
    if (hasViewPermission || hasUpdatePermission) {
        defaultAvailableActions = ['actions', 'edit', ...defaultAvailableActions];
    }
    const availableActions = defaultAvailableActions.filter(action => !excludedActions.includes(action));

    return (
        <>
            <GenericPage
                title="Cash Memos"
                moduleName="CashMemo"
                moduleKey="cashmemos"
                state={cashmemoState}
                actions={actions}
                FormComponent={CashMemoForm}
                columns={columns}
                availableActions={availableActions}
                actionsColumnWidth="150px"
                urlKey="cash_memos"
                actionType="url"
                onSelectedRowsChange={onSelectedRowsChange}

                selectedIds={selectedIds}
                externalCondition={externalCondition}
            />
        </>
    );
};

export default Cashmemos; 